import { useState, useEffect, useCallback } from "react";
import { Heart, ArrowRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PRESETS = [
  { id: "essentials", amount: 25, label: "Essentials", impact: "Groceries for a family in need." },
  { id: "respite", amount: 75, label: "Respite", impact: "A caregiver respite session." },
  { id: "mentorship", amount: 150, label: "Mentorship", impact: "A youth mentorship month." },
  { id: "outreach", amount: 500, label: "Outreach", impact: "Veteran family outreach." },
];

// Lightweight controlled modal (no shadcn Dialog dependency to keep styling cohesive)
export default function DonateModal({ open, onClose }) {
  const [selectedId, setSelectedId] = useState("respite");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const closeIfNotLoading = useCallback(() => {
    if (!loading) onClose?.();
  }, [loading, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeIfNotLoading();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, closeIfNotLoading]);

  if (!open) return null;

  const isCustom = selectedId === "custom";

  const handleDonate = async () => {
    if (loading) return;

    const payload = {
      origin_url: window.location.origin,
      donor_name: donorName.trim() || undefined,
      donor_email: donorEmail.trim() || undefined,
    };

    if (isCustom) {
      const amt = parseFloat(customAmount);
      if (!amt || amt < 5) {
        toast.error("Please enter at least $5.");
        return;
      }
      payload.custom_amount = Math.round(amt * 100) / 100;
    } else {
      payload.package_id = selectedId;
    }

    setLoading(true);
    try {
      const { url } = await api.createDonationCheckout(payload);
      if (!url) throw new Error("No checkout URL returned");
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="donate-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
    >
      <button
        aria-label="Close"
        onClick={closeIfNotLoading}
        data-testid="donate-modal-backdrop"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="donate-modal-title"
        className="relative w-full max-w-2xl bg-[#FAF8F5] rounded-3xl border border-[#E2DCD0] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-4 px-7 sm:px-9 pt-7 pb-2">
          <div>
            <div className="inline-flex items-center gap-2 mb-3">
              <Heart size={14} className="text-[#692128]" />
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B2635]">
                Make a Gift
              </span>
            </div>
            <h3
              id="donate-modal-title"
              className="font-serif text-3xl sm:text-4xl text-[#2C2A29] leading-tight"
            >
              Support Guardian Grace
            </h3>
            <p className="mt-2 text-sm text-[#5C5856]">
              Choose an impact level — or give any amount your heart leads.
            </p>
          </div>
          <button
            onClick={closeIfNotLoading}
            data-testid="donate-modal-close"
            className="shrink-0 -mt-1 -mr-2 p-2 rounded-full text-[#5C5856] hover:bg-[#E2DCD0]/60 transition-colors"
            aria-label="Close donation dialog"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-7 sm:px-9 py-5 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            {PRESETS.map((p) => {
              const active = selectedId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  data-testid={`donate-preset-${p.id}`}
                  className={`text-left rounded-2xl border p-4 transition-all ${
                    active
                      ? "border-[#692128] bg-[#692128] text-[#F4F1EB] shadow-md"
                      : "border-[#E2DCD0] bg-white hover:border-[#C89F5D] text-[#2C2A29]"
                  }`}
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-2xl">${p.amount}</span>
                    <span
                      className={`text-[10px] tracking-[0.18em] uppercase font-bold ${
                        active ? "text-[#C89F5D]" : "text-[#8B2635]"
                      }`}
                    >
                      {p.label}
                    </span>
                  </div>
                  <p
                    className={`mt-1 text-xs leading-snug ${
                      active ? "text-[#F4F1EB]/85" : "text-[#5C5856]"
                    }`}
                  >
                    {p.impact}
                  </p>
                </button>
              );
            })}

            <button
              onClick={() => setSelectedId("custom")}
              data-testid="donate-preset-custom"
              className={`col-span-2 rounded-2xl border p-4 transition-all text-left ${
                isCustom
                  ? "border-[#692128] bg-[#692128] text-[#F4F1EB]"
                  : "border-dashed border-[#C89F5D] bg-white hover:bg-[#FAF6EE] text-[#2C2A29]"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-xl">Custom amount</span>
                <span
                  className={`text-[10px] tracking-[0.18em] uppercase font-bold ${
                    isCustom ? "text-[#C89F5D]" : "text-[#8B2635]"
                  }`}
                >
                  Give your way
                </span>
              </div>
            </button>
          </div>

          {isCustom && (
            <div className="mt-4">
              <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                Amount (USD)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5856] font-serif text-lg">
                  $
                </span>
                <input
                  type="number"
                  min="5"
                  step="1"
                  inputMode="decimal"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="100"
                  data-testid="donate-custom-amount"
                  className="w-full pl-9 pr-4 py-3 rounded-xl border border-[#E2DCD0] bg-white focus:border-[#692128] focus:outline-none text-lg font-serif text-[#2C2A29]"
                />
              </div>
              <p className="mt-1.5 text-xs text-[#5C5856]">Minimum $5.</p>
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                Name (optional)
              </label>
              <input
                type="text"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="Jane Smith"
                data-testid="donate-donor-name"
                className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.18em] uppercase font-bold text-[#8B2635] mb-2">
                Email (optional)
              </label>
              <input
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="donate-donor-email"
                className="w-full px-4 py-3 rounded-xl border border-[#E2DCD0] bg-white focus:border-[#692128] focus:outline-none text-[#2C2A29]"
              />
            </div>
          </div>
        </div>

        <div className="px-7 sm:px-9 py-5 border-t border-[#E2DCD0] bg-[#F4F1EB] flex items-center justify-between gap-4">
          <p className="text-xs text-[#5C5856] hidden sm:block">
            Secure checkout powered by Stripe.
          </p>
          <button
            onClick={handleDonate}
            disabled={loading}
            data-testid="donate-modal-submit"
            className="group inline-flex items-center gap-2 bg-[#692128] text-[#F4F1EB] hover:bg-[#8B2635] disabled:opacity-70 disabled:cursor-not-allowed rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-all"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                Continue to secure checkout
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
